import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CheckCircle2, Clock, MoreVertical, Plus, Send } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import GeneratedDocumentsList from '../components/GeneratedDocumentsList'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineTypeahead,
  InlineStatus,
  RecordDrawer,
  RowActions,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'
import { SearchableSelect } from '@/components/SearchableSelect'

// ─────────────────── Types ───────────────────
type Employment = {
  id: number
  person: number
  person_name: string
  subsidiary: number | null
  subsidiary_name: string | null
  subsidiary_code: string | null
  country_code: string
  governing_country: string
  employee_number: string
  start_date: string | null
  end_date: string | null
  end_reason: string
  status: 'PRE_HIRE' | 'ACTIVE' | 'ON_LEAVE' | 'ENDED'
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'INTERN'
  contract_type: number | null
  contract_type_code: string | null
  contract_type_name: string | null
  probation_end_date: string | null
  notice_period_days: number | null
  working_hours_per_week: string | null
  working_hours_per_day: string | null
  working_hours_schedule: string
  duration_type: 'DETERMINED' | 'UNDETERMINED'
  authority_registration_number: string
  job_title: string
  isco_08_code: string
  isco_08_title: string
  department: number | null
  department_name: string | null
  manager: number | null
  manager_name: string | null
  manager_via_position: { code: string; title: string } | null
  position: number | null
  position_code: string | null
  position_title: string | null
  previous_employment: number | null
  notes: string
  is_currently_active: boolean
  legal_entity: LegalEntity | null
  // Onboarding lifecycle
  contract_signed_date: string | null
  onboarding_started_at: string | null
  // Role fields
  position_roles: RoleSummary[]
  additional_roles: number[]          // list of role IDs (from __all__)
}

type RoleSummary = {
  id: number
  name: string
  description: string
  permission_codes: string[]
}

type LegalEntity = {
  type: 'subsidiary' | 'organization'
  subsidiary_id: number | null
  code: string | null
  name: string
  legal_form: string
  registration_number: string
  tax_id: string
  vat_number: string
  country_code: string
}

type Department = { id: number; code: string; name: string; is_active: boolean }
type Subsidiary = { id: number; code: string; name: string; country_code: string }
type PositionLite = {
  id: number
  code: string
  title: string
  is_active: boolean
  is_open: boolean
  current_employment_id: number | null
}
type ContractType = { id: number; code: string; name: string; country: number | null }
type IscoCode = { id: number; code: string; title: string; level: number }

type TimeOff = {
  id: number
  employee: number | null
  employee_name: string | null
  employment: number | null
  person_name: string | null
  employment_number: string | null
  leave_type: 'VACATION' | 'SICK' | 'UNPAID' | 'PARENTAL' | 'OTHER'
  start_date: string
  end_date: string
  days: string
  reason: string
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
}

type Compensation = {
  id: number
  employment: number
  employment_number: string
  person_name: string
  compensation_type: 'SALARY' | 'BONUS' | 'ALLOWANCE' | 'COMMISSION'
  amount: string
  currency_code: string
  frequency: 'ANNUAL' | 'MONTHLY' | 'DAILY' | 'HOURLY' | 'ONEOFF'
  gross_net: 'GROSS' | 'NET'
  effective_from: string
  effective_to: string | null
  reason: string
  notes: string
  is_currently_active: boolean
}

type CompInput = {
  compensation_type: Compensation['compensation_type']
  amount: string
  currency_code: string
  frequency: Compensation['frequency']
  gross_net: Compensation['gross_net']
  effective_from: string
  effective_to: string | null
  reason: string
  notes: string
}

type LeaveBalance = {
  id: number
  employment: number
  employment_number: string
  person_name: string
  year: number
  leave_type: 'VACATION' | 'SICK' | 'UNPAID' | 'PARENTAL' | 'OTHER'
  entitlement_days: string
  carried_over_days: string
  adjusted_days: string
  taken_days: string
  balance_days: string
  notes: string
}

type LeaveBalanceInput = {
  year: number
  leave_type: LeaveBalance['leave_type']
  entitlement_days: string
  carried_over_days: string
  adjusted_days: string
  notes: string
}

type EmploymentDocument = {
  id: number
  employment: number
  employment_number: string
  person_name: string
  document_type:
    | 'OFFER_LETTER' | 'CONTRACT' | 'CONTRACT_AMENDMENT' | 'NDA'
    | 'ID_COPY' | 'CERTIFICATE' | 'TAX_FORM' | 'PERFORMANCE_REVIEW' | 'OTHER'
  title: string
  file_url: string
  issued_date: string | null
  expiry_date: string | null
  notes: string
  expiry_status: 'NO_EXPIRY' | 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED'
}

type DocInput = {
  document_type: EmploymentDocument['document_type']
  title: string
  file_url: string
  issued_date: string | null
  expiry_date: string | null
  notes: string
}

type ProjectAssignment = {
  id: number
  project: number
  project_code: string
  project_name: string
  employee: number | null
  employee_name: string | null
  employment: number | null
  person_name: string | null
  employment_number: string | null
  role: 'CONSULTANT' | 'CONTRACTOR' | 'MANAGER'
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
}

const STATUS_COLOR: Record<Employment['status'], string> = {
  PRE_HIRE: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  ON_LEAVE: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-slate-100 text-slate-700',
}

const EMPLOYMENT_TYPE_LABEL: Record<Employment['employment_type'], string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  INTERN: 'Intern',
}

const END_REASON_LABEL: Record<string, string> = {
  RESIGNED: 'Resigned',
  TERMINATED: 'Terminated',
  CONTRACT_END: 'Contract end',
  RETIRED: 'Retired',
  TRANSFER: 'Transferred to another entity',
  OTHER: 'Other',
}

const LEAVE_LABEL: Record<TimeOff['leave_type'], string> = {
  VACATION: 'Vacation',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  PARENTAL: 'Parental leave',
  OTHER: 'Other',
}

const LEAVE_STATUS_COLOR: Record<TimeOff['status'], string> = {
  REQUESTED: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
}

const ASSIGNMENT_STATUS_COLOR: Record<ProjectAssignment['status'], string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-slate-100 text-slate-700',
}

const COMP_TYPE_LABEL: Record<Compensation['compensation_type'], string> = {
  SALARY: 'Base salary',
  BONUS: 'Bonus',
  ALLOWANCE: 'Allowance',
  COMMISSION: 'Commission',
}

const COMP_FREQUENCY_LABEL: Record<Compensation['frequency'], string> = {
  ANNUAL: 'Annual',
  MONTHLY: 'Monthly',
  DAILY: 'Daily',
  HOURLY: 'Hourly',
  ONEOFF: 'One-off bonus',
}

const COMP_GROSS_NET_LABEL: Record<Compensation['gross_net'], string> = {
  GROSS: 'Gross',
  NET: 'Net',
}

const DURATION_TYPE_LABEL: Record<Employment['duration_type'], string> = {
  DETERMINED: 'Determined (fixed-term)',
  UNDETERMINED: 'Undetermined (permanent)',
}

const COMP_REASON_LABEL: Record<string, string> = {
  HIRE: 'Hire',
  PROMOTION: 'Promotion',
  MERIT: 'Merit increase',
  MARKET_ADJUSTMENT: 'Market adjustment',
  CONTRACT_RENEWAL: 'Contract renewal',
  OTHER: 'Other',
}

const DOC_TYPE_LABEL: Record<EmploymentDocument['document_type'], string> = {
  OFFER_LETTER: 'Offer letter',
  CONTRACT: 'Contract',
  CONTRACT_AMENDMENT: 'Contract amendment',
  NDA: 'NDA',
  ID_COPY: 'ID copy',
  CERTIFICATE: 'Certificate',
  TAX_FORM: 'Tax form',
  PERFORMANCE_REVIEW: 'Performance review',
  OTHER: 'Other',
}

const DOC_EXPIRY_COLOR: Record<EmploymentDocument['expiry_status'], string> = {
  NO_EXPIRY: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  EXPIRING_SOON: 'bg-amber-100 text-amber-800',
  EXPIRED: 'bg-red-100 text-red-700',
}

const DOC_EXPIRY_LABEL: Record<EmploymentDocument['expiry_status'], string> = {
  NO_EXPIRY: 'No expiry',
  ACTIVE: 'Active',
  EXPIRING_SOON: 'Expiring soon',
  EXPIRED: 'Expired',
}

const LEAVE_LABEL_FULL: Record<LeaveBalance['leave_type'], string> = {
  VACATION: 'Vacation',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  PARENTAL: 'Parental leave',
  OTHER: 'Other',
}

type TabKey = 'contract' | 'role' | 'compensation' | 'leaves' | 'documents' | 'timeoff' | 'projects'

type DocsFilter = 'hr' | 'generated' | 'attachments'

// ─────────────────── Page ───────────────────
export default function HREmploymentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const employmentId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const employmentQ = useQuery({
    queryKey: ['hr', 'employment', employmentId],
    queryFn: async () => (await api.get<Employment>(`/hr/employments/${employmentId}/`)).data,
    enabled: !!employmentId,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
  })
  const contractTypesQ = useQuery({
    queryKey: ['contract-types'],
    queryFn: async () => (await api.get<ContractType[]>('/contract-types/')).data,
  })
  const positionsQ = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: async () => (await api.get<PositionLite[]>('/hr/positions/')).data,
  })

  const timeOffQ = useQuery({
    queryKey: ['hr', 'time-off', { employment: employmentId }],
    queryFn: async () =>
      (await api.get<TimeOff[]>(`/hr/time-off/?employment=${employmentId}`)).data,
    enabled: !!employmentId,
  })
  const assignmentsQ = useQuery({
    queryKey: ['consulting', 'assignments', { employment: employmentId }],
    queryFn: async () =>
      (await api.get<ProjectAssignment[]>(`/consulting/assignments/?employment=${employmentId}`)).data,
    enabled: !!employmentId,
  })
  const compensationsQ = useQuery({
    queryKey: ['hr', 'compensations', { employment: employmentId }],
    queryFn: async () =>
      (await api.get<Compensation[]>(`/hr/compensations/?employment=${employmentId}`)).data,
    enabled: !!employmentId,
  })
  const leaveBalancesQ = useQuery({
    queryKey: ['hr', 'leave-balances', { employment: employmentId }],
    queryFn: async () =>
      (await api.get<LeaveBalance[]>(`/hr/leave-balances/?employment=${employmentId}`)).data,
    enabled: !!employmentId,
  })
  const documentsQ = useQuery({
    queryKey: ['hr', 'employment-documents', { employment: employmentId }],
    queryFn: async () =>
      (await api.get<EmploymentDocument[]>(`/hr/employment-documents/?employment=${employmentId}`)).data,
    enabled: !!employmentId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Employment>) =>
      (await api.patch(`/hr/employments/${employmentId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employment', employmentId] })
      qc.invalidateQueries({ queryKey: ['hr', 'employments'] })
      qc.invalidateQueries({ queryKey: ['hr', 'person'] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/employments/${employmentId}/`),
    onSuccess: () => {
      toast.success('Employment deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'employments'] })
      if (employmentQ.data?.person) {
        navigate(`/admin/hr/people/${employmentQ.data.person}`)
      } else {
        navigate('/admin/hr/people')
      }
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('contract')
  const [docsFilter, setDocsFilter] = useState<DocsFilter>('hr')

  if (employmentQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (employmentQ.isError || !employmentQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Employment not found.</p>
          <Link to="/admin/hr/people" className="text-blue-600 hover:underline">← Back to people</Link>
        </div>
      </AdminLayout>
    )
  }

  const employment = employmentQ.data
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const subsidiaries = subsidiariesQ.data || []
  const contractTypes = contractTypesQ.data || []
  const positions = positionsQ.data || []
  const timeOff = timeOffQ.data || []
  const assignments = assignmentsQ.data || []
  const compensations = compensationsQ.data || []
  const leaveBalances = leaveBalancesQ.data || []
  const documents = documentsQ.data || []

  const save = (payload: Partial<Employment>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Page header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link
            to={`/admin/hr/people/${employment.person}`}
            className="text-muted-foreground hover:text-foreground hover:underline"
          >
            ← {employment.person_name}
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{employment.employee_number || '—'}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={employment.job_title} placeholder="Add job title" onSave={(v) => save({ job_title: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {EMPLOYMENT_TYPE_LABEL[employment.employment_type]}
              {employment.subsidiary_name
                ? <> · {employment.subsidiary_code} — {employment.subsidiary_name}</>
                : <> · Group entity</>}
              {employment.governing_country && <> · {employment.governing_country}</>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={employment.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'PRE_HIRE', label: 'Pre-hire' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'ON_LEAVE', label: 'On leave' },
                { value: 'ENDED', label: 'Ended' },
              ]}
              onSave={(v) => save({ status: v as Employment['status'] })}
            />
            <GenerateDocumentButton sourceType="hr.Employment" sourceId={employment.id} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`employment ${employment.employee_number}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete employment</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['contract', 'role', 'compensation', 'leaves', 'documents', 'timeoff', 'projects'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'contract' && 'Contract'}
              {t === 'role' && 'Role'}
              {t === 'compensation' && `Compensation (${compensations.length})`}
              {t === 'leaves' && `Leave Balance (${leaveBalances.length})`}
              {t === 'documents' && 'Documents'}
              {t === 'timeoff' && `Time Off (${timeOff.length})`}
              {t === 'projects' && `Projects (${assignments.length})`}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'contract' && (
        <ContractTab
          employment={employment}
          subsidiaries={subsidiaries}
          contractTypes={contractTypes}
          departments={departments}
          positions={positions}
          onSave={save}
        />
      )}
      {tab === 'role' && (
        <RoleTab
          employment={employment}
          onSave={save}
        />
      )}
      {tab === 'compensation' && (
        <CompensationTab
          employmentId={employmentId}
          rows={compensations}
          loading={compensationsQ.isLoading}
        />
      )}
      {tab === 'leaves' && (
        <LeaveBalanceTab
          employmentId={employmentId}
          rows={leaveBalances}
          loading={leaveBalancesQ.isLoading}
        />
      )}
      {tab === 'documents' && (
        <div className="space-y-3">
          {/* Filter pills */}
          <div className="inline-flex items-center rounded-md border bg-card p-1 text-sm">
            {([
              { key: 'hr',          label: 'HR Records', count: documents.length },
              { key: 'generated',   label: 'Generated' },
              { key: 'attachments', label: 'Attachments' },
            ] as { key: DocsFilter; label: string; count?: number }[]).map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setDocsFilter(p.key)}
                className={`px-3 py-1.5 rounded ${
                  docsFilter === p.key
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {p.label}{p.count !== undefined ? ` (${p.count})` : ''}
              </button>
            ))}
          </div>

          {docsFilter === 'hr' && (
            <DocumentsTab
              employmentId={employmentId}
              rows={documents}
              loading={documentsQ.isLoading}
            />
          )}
          {docsFilter === 'generated' && (
            <GeneratedDocumentsList sourceType="hr.Employment" sourceId={employmentId} />
          )}
          {docsFilter === 'attachments' && (
            <FilesTab parent="hr.employment" parentId={employmentId} />
          )}
        </div>
      )}
      {tab === 'timeoff' && <TimeOffTab rows={timeOff} loading={timeOffQ.isLoading} />}
      {tab === 'projects' && <ProjectsTab rows={assignments} loading={assignmentsQ.isLoading} />}
    </AdminLayout>
  )
}

// ─────────────────── Onboarding card ───────────────────

function OnboardingCard({ employment }: { employment: Employment }) {
  const qc = useQueryClient()
  const [confirmSignOpen, setConfirmSignOpen] = useState(false)

  const signMut = useMutation({
    mutationFn: async (signedDate?: string) =>
      (await api.post(`/hr/employments/${employment.id}/mark-contract-signed/`, signedDate ? { signed_date: signedDate } : {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employment', employment.id] })
      toast.success('Contract marked as signed')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const onboardMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/hr/employments/${employment.id}/start-onboarding/`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employment', employment.id] })
      toast.success('Onboarding started — welcome email sent')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const signed = employment.contract_signed_date
  const started = employment.onboarding_started_at

  // ── Done state ──
  if (started) {
    return (
      <div className="lg:col-span-2 rounded-lg border border-green-200 bg-green-50 px-6 py-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-green-800 text-sm">Onboarding started</span>
          <span className="text-green-700 text-sm ml-2">
            Welcome email sent on {new Date(started).toLocaleDateString()}
          </span>
          {signed && (
            <span className="text-green-600 text-xs ml-3">· Contract signed {signed}</span>
          )}
        </div>
      </div>
    )
  }

  // ── Signed, awaiting start ──
  if (signed) {
    return (
      <div className="lg:col-span-2 rounded-lg border border-blue-200 bg-blue-50 px-6 py-4 flex items-center gap-4">
        <CheckCircle2 className="h-5 w-5 text-blue-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-blue-800 text-sm">Contract signed</span>
          <span className="text-blue-700 text-sm ml-2">{signed}</span>
          <p className="text-xs text-blue-600 mt-0.5">
            Click "Start Onboarding" when you are ready to send the welcome email and activate the employee portal account.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => onboardMut.mutate()}
          disabled={onboardMut.isPending}
          className="shrink-0"
        >
          <Send className="h-3 w-3 mr-1.5" />
          {onboardMut.isPending ? 'Sending…' : 'Start Onboarding'}
        </Button>
      </div>
    )
  }

  // ── Awaiting signature ──
  return (
    <>
      <div className="lg:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-6 py-4 flex items-center gap-4">
        <Clock className="h-5 w-5 text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-amber-800 text-sm">Awaiting contract signature</span>
          <p className="text-xs text-amber-700 mt-0.5">
            Once the candidate has signed, mark it here. The "Start Onboarding" button will appear so you can send the welcome email when ready.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setConfirmSignOpen(true)}
          disabled={signMut.isPending}
          className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
        >
          {signMut.isPending ? 'Saving…' : 'Mark Contract Signed'}
        </Button>
      </div>

      <AlertDialog open={confirmSignOpen} onOpenChange={setConfirmSignOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark contract as signed?</AlertDialogTitle>
            <AlertDialogDescription>
              This records today as the contract signature date for{' '}
              <strong>{employment.person_name}</strong>. The "Start Onboarding"
              button will become available. You can still correct the date via
              the contract_signed_date field if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => signMut.mutate(undefined)}>
              Confirm — contract is signed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ─────────────────── Contract tab ───────────────────

function ContractTab({
  employment, subsidiaries, contractTypes, departments, positions, onSave,
}: {
  employment: Employment
  subsidiaries: Subsidiary[]
  contractTypes: ContractType[]
  departments: Department[]
  positions: PositionLite[]
  onSave: (payload: Partial<Employment>) => Promise<void>
}) {
  const legalEntity = employment.legal_entity

  const isSingleCompany = legalEntity?.type === 'organization'
  // Edit deeplink: subsidiaries have their own detail page, the org has the
  // settings page. Used by the "Edit on …" link in the Identity card.
  const editEntityPath = isSingleCompany
    ? '/admin/organization'
    : (legalEntity?.subsidiary_id ? `/admin/organization/subsidiaries/${legalEntity.subsidiary_id}` : '/admin/organization')
  const editEntityLabel = isSingleCompany ? 'Organization page' : 'Subsidiary page'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <OnboardingCard employment={employment} />

      <Card title="Structure">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Hiring entity">
            {isSingleCompany ? (
              <div className="text-sm">
                <span className="text-muted-foreground">Group entity</span>
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">
                  Single Company mode
                </span>
                <div className="text-xs text-muted-foreground mt-1">
                  No subsidiary — the Organization itself is the legal entity. See Identity →.
                </div>
              </div>
            ) : (
              <InlineSelect
                value={employment.subsidiary ? String(employment.subsidiary) : ''}
                displayLabel={
                  employment.subsidiary_name
                    ? `${employment.subsidiary_code} — ${employment.subsidiary_name}`
                    : <span className="text-muted-foreground">— pick a subsidiary —</span>
                }
                allowClear
                options={subsidiaries.map((s) => ({
                  value: String(s.id), label: `${s.code} — ${s.name}`, searchText: `${s.code} ${s.name}`,
                }))}
                onSave={(v) => onSave({ subsidiary: v ? Number(v) : null })}
                helper="The legal entity that employs this person. Country is derived from this choice."
              />
            )}
          </Row>
          <Row label="Governing country">
            <span className="font-mono text-xs uppercase">{employment.governing_country || <Dash />}</span>
          </Row>
        </dl>
      </Card>

      <Card title="Position & Job">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Position">
            {employment.position_title
              ? [employment.position_code, employment.position_title].filter(Boolean).join(' — ')
              : <Dash />}
          </Row>
          <Row label="ISCO-08">
            {employment.governing_country ? (
              <InlineTypeahead
                value={employment.isco_08_code || ''}
                displayLabel={
                  employment.isco_08_code
                    ? `${employment.isco_08_code} — ${employment.isco_08_title || employment.isco_08_code}`
                    : <Dash />
                }
                allowClear
                loadOptions={async (q) => {
                  const { data } = await api.get<IscoCode[]>(
                    `/isco-codes/?country_code=${employment.governing_country}&leaf_only=1&search=${encodeURIComponent(q)}`
                  )
                  return data.map((c) => ({
                    value: c.code,
                    label: `${c.code} — ${c.title}`,
                    searchText: `${c.code} ${c.title}`,
                    meta: { title: c.title },
                  }))
                }}
                onSelect={async (opt) => {
                  await onSave({ isco_08_code: opt.value, isco_08_title: opt.meta?.title ?? '' })
                }}
                helper={`ISCO-08 occupation codes for ${employment.governing_country}. Type to search.`}
              />
            ) : (
              employment.isco_08_code
                ? `${employment.isco_08_code}${employment.isco_08_title ? ` — ${employment.isco_08_title}` : ''}`
                : <Dash />
            )}
          </Row>
          <Row label="Department">
            {employment.department_name || <Dash />}
          </Row>
          <Row label="Manager">
            <div>
              <div>{employment.manager_name || <Dash />}</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {employment.manager_via_position
                  ? `Derived from the parent of this position (${employment.manager_via_position.code} — ${employment.manager_via_position.title}). Change it via the position's "Reports to" seat.`
                  : "Derived from the parent of this employment's position. Set a linked position with a parent seat to populate it."}
              </p>
            </div>
          </Row>
        </dl>
      </Card>

      <Card title="Company">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Employment #">
            <InlineText
              value={employment.employee_number}
              onSave={(v) => onSave({ employee_number: v })}
              placeholder="Required unique number"
            />
          </Row>
          <Row label="Legal name">
            {legalEntity?.name || <Dash />}
          </Row>
          <Row label="Legal form">
            {legalEntity?.legal_form || <Dash />}
          </Row>
          <Row label="Registration #">
            {legalEntity?.registration_number || <Dash />}
          </Row>
          <Row label="Tax ID">
            {legalEntity?.tax_id || <Dash />}
          </Row>
          <Row label="VAT #">
            {legalEntity?.vat_number || <Dash />}
          </Row>
        </dl>
      </Card>

      <Card title="Lifecycle">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Start date">
            <InlineDate value={employment.start_date} onSave={(v) => onSave({ start_date: v })} />
          </Row>
          <Row label="End date">
            <InlineDate value={employment.end_date} onSave={(v) => onSave({ end_date: v })} />
          </Row>
          <Row label="End reason">
            <InlineSelect
              value={employment.end_reason || ''}
              displayLabel={employment.end_reason ? END_REASON_LABEL[employment.end_reason] : <Dash />}
              allowClear
              options={Object.entries(END_REASON_LABEL).map(([k, v]) => ({ value: k, label: v }))}
              onSave={(v) => onSave({ end_reason: v })}
              helper="Required when end date is set."
            />
          </Row>
        </dl>
      </Card>

      <Card title="Contract" className="lg:col-span-2">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Employment type">
            <InlineSelect
              value={employment.employment_type}
              options={(Object.keys(EMPLOYMENT_TYPE_LABEL) as Employment['employment_type'][]).map((k) => ({
                value: k, label: EMPLOYMENT_TYPE_LABEL[k],
              }))}
              onSave={(v) => onSave({ employment_type: v as Employment['employment_type'] })}
            />
          </Row>
          <Row label="Duration">
            <InlineSelect
              value={employment.duration_type}
              options={(Object.keys(DURATION_TYPE_LABEL) as Employment['duration_type'][]).map((k) => ({
                value: k, label: DURATION_TYPE_LABEL[k],
              }))}
              onSave={(v) => onSave({ duration_type: v as Employment['duration_type'] })}
              helper="Determined = fixed-term (end date required); Undetermined = permanent."
            />
          </Row>
          <Row label="Contract type">
            <InlineSelect
              value={employment.contract_type ? String(employment.contract_type) : ''}
              displayLabel={
                employment.contract_type_name
                  ? `${employment.contract_type_code} — ${employment.contract_type_name}`
                  : <Dash />
              }
              allowClear
              options={contractTypes.map((c) => ({
                value: String(c.id),
                label: `${c.code} — ${c.name}`,
                searchText: `${c.code} ${c.name}`,
              }))}
              onSave={(v) => onSave({ contract_type: v ? Number(v) : null })}
              helper="Country-specific contract type (e.g. CDI, CDD, FULL_TIME)."
            />
          </Row>
          <Row label="Authority reg. #">
            <InlineText
              value={employment.authority_registration_number}
              onSave={(v) => onSave({ authority_registration_number: v })}
              placeholder="e.g. REVISAL ID, SEPE number"
            />
          </Row>
          <Row label="Probation ends">
            <InlineDate value={employment.probation_end_date} onSave={(v) => onSave({ probation_end_date: v })} />
          </Row>
          <Row label="Notice period (days)">
            <InlineNumber
              value={employment.notice_period_days != null ? String(employment.notice_period_days) : ''}
              onSave={(v) => onSave({ notice_period_days: v === '' ? null : Number(v) })}
              placeholder="Inherit from contract type"
            />
          </Row>
          <Row label="Working hours / week">
            <InlineNumber
              value={employment.working_hours_per_week || ''}
              onSave={(v) => onSave({ working_hours_per_week: v === '' ? null : v })}
              placeholder="Add weekly hours"
            />
          </Row>
          <Row label="Working hours / day">
            <InlineNumber
              value={employment.working_hours_per_day || ''}
              onSave={(v) => onSave({ working_hours_per_day: v === '' ? null : v })}
              placeholder="e.g. 8"
            />
          </Row>
          <Row label="Working schedule">
            <InlineText
              value={employment.working_hours_schedule}
              onSave={(v) => onSave({ working_hours_schedule: v })}
              placeholder="e.g. 09:00-17:00"
            />
          </Row>
          <Row label="Notes">
            <InlineText
              value={employment.notes}
              multiline
              onSave={(v) => onSave({ notes: v })}
              placeholder="Contract-specific notes"
            />
          </Row>
        </dl>
      </Card>
    </div>
  )
}

// ─────────────────── Role tab ───────────────────

function RoleTab({
  employment, onSave,
}: {
  employment: Employment
  onSave: (payload: Partial<Employment>) => Promise<void>
}) {
  const qc = useQueryClient()

  // All available roles for the Add drawer picker
  const rolesQ = useQuery({
    queryKey: ['core', 'roles'],
    queryFn: async () => (await api.get<RoleSummary[]>('/roles/')).data,
  })

  // Additional role drawer state
  const [addingRole, setAddingRole] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState('')

  // Roles already assigned (avoid showing them again in the picker)
  const additionalRoleIds = new Set(employment.additional_roles ?? [])
  const positionRoleIds = new Set((employment.position_roles ?? []).map((r) => r.id))

  const availableRoles = (rolesQ.data ?? []).filter(
    (r) => !additionalRoleIds.has(r.id) && !positionRoleIds.has(r.id),
  )

  const addRoleMut = useMutation({
    mutationFn: async (roleId: number) =>
      (await api.patch(`/hr/employments/${employment.id}/`, {
        additional_role_ids: [...employment.additional_roles, roleId],
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employment', employment.id] })
      toast.success('Role added')
      setAddingRole(false)
      setSelectedRoleId('')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const removeRoleMut = useMutation({
    mutationFn: async (roleId: number) =>
      (await api.patch(`/hr/employments/${employment.id}/`, {
        additional_role_ids: employment.additional_roles.filter((id) => id !== roleId),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'employment', employment.id] })
      toast.success('Role removed')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <div className="space-y-6 max-w-3xl">
      {/* ── Default Position Roles ──────────────────────────────── */}
      <div className="bg-card rounded-lg shadow p-6">
        <h3 className="font-semibold text-sm mb-1">Default Position Roles</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Inherited automatically from the linked position. To change them, edit the position.
        </p>
        {!employment.position ? (
          <p className="text-sm text-muted-foreground italic">No position linked — no inherited roles.</p>
        ) : (employment.position_roles ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No roles configured on this position.</p>
        ) : (
          <div className="space-y-2">
            {(employment.position_roles ?? []).map((role) => (
              <RoleCard key={role.id} role={role} />
            ))}
          </div>
        )}
      </div>

      {/* ── Additional Roles ────────────────────────────────────── */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="font-semibold text-sm">Additional Roles</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Extra roles assigned by HR on top of what the position provides.
            </p>
          </div>
          <Button size="sm" onClick={() => setAddingRole(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add role
          </Button>
        </div>

        {(employment.additional_roles ?? []).length === 0 ? (
          <p className="px-6 py-8 text-sm text-muted-foreground text-center italic">
            No additional roles assigned.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Role</Th>
                <Th>Permissions</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(employment.additional_roles ?? []).map((roleId) => {
                const role = (rolesQ.data ?? []).find((r) => r.id === roleId)
                return (
                  <tr key={roleId}>
                    <Td>
                      <div className="font-medium text-sm">{role?.name ?? `Role #${roleId}`}</div>
                      {role?.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{role.description}</div>
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {(role?.permission_codes ?? []).slice(0, 5).map((code) => (
                          <span key={code} className="text-xs bg-muted rounded px-1.5 py-0.5 font-mono">{code}</span>
                        ))}
                        {(role?.permission_codes ?? []).length > 5 && (
                          <span className="text-xs text-muted-foreground">+{role!.permission_codes.length - 5} more</span>
                        )}
                      </div>
                    </Td>
                    <Td className="text-right">
                      <button
                        type="button"
                        onClick={() => removeRoleMut.mutate(roleId)}
                        disabled={removeRoleMut.isPending}
                        className="text-xs text-destructive hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add role drawer ─────────────────────────────────────── */}
      <RecordDrawer
        open={addingRole}
        onOpenChange={(v) => { if (!v) { setAddingRole(false); setSelectedRoleId('') } }}
        title="Add additional role"
        description="Grant an extra system role to this employment. The role will be synced to the employee's portal account immediately."
        onSubmit={() => { if (selectedRoleId) addRoleMut.mutate(Number(selectedRoleId)) }}
        isPending={addRoleMut.isPending}
        submitLabel="Add role"
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Role</label>
            <SearchableSelect
              value={selectedRoleId}
              onChange={setSelectedRoleId}
              placeholder="Search roles…"
              options={availableRoles.map((r) => ({
                value: String(r.id),
                label: r.name,
                hint: r.description || undefined,
                keywords: r.permission_codes.join(' '),
              }))}
              emptyLabel={rolesQ.isLoading ? 'Loading…' : 'No roles available'}
            />
          </div>
          {selectedRoleId && (() => {
            const role = availableRoles.find((r) => r.id === Number(selectedRoleId))
            return role && role.permission_codes.length > 0 ? (
              <div className="rounded border border-border p-3 bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Permissions granted</p>
                <div className="flex flex-wrap gap-1">
                  {role.permission_codes.map((code) => (
                    <span key={code} className="text-xs bg-card rounded px-1.5 py-0.5 font-mono border border-border">{code}</span>
                  ))}
                </div>
              </div>
            ) : null
          })()}
        </div>
      </RecordDrawer>
    </div>
  )
}

/** Compact card for a single role — used in both position-inherited and additional sections. */
function RoleCard({ role }: { role: RoleSummary }) {
  return (
    <div className="rounded-lg border border-border p-3 bg-background">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-sm font-medium">{role.name}</span>
          {role.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {role.permission_codes.length} permission{role.permission_codes.length !== 1 ? 's' : ''}
        </span>
      </div>
      {role.permission_codes.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {role.permission_codes.map((code) => (
            <span key={code} className="text-xs bg-muted rounded px-1.5 py-0.5 font-mono">{code}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────── Time Off tab ───────────────────

function TimeOffTab({ rows, loading }: { rows: TimeOff[]; loading: boolean }) {
  if (loading) return <p className="text-muted-foreground">Loading…</p>
  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
        No time-off records under this employment.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Type</Th>
            <Th>Start</Th>
            <Th>End</Th>
            <Th>Days</Th>
            <Th>Reason</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-muted/40">
              <Td className="text-xs">{LEAVE_LABEL[r.leave_type]}</Td>
              <Td className="text-xs">{r.start_date}</Td>
              <Td className="text-xs">{r.end_date}</Td>
              <Td className="text-sm">{r.days}</Td>
              <Td className="text-sm">{r.reason || <Dash />}</Td>
              <Td>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${LEAVE_STATUS_COLOR[r.status]}`}>
                  {r.status}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────── Projects tab ───────────────────

function ProjectsTab({ rows, loading }: { rows: ProjectAssignment[]; loading: boolean }) {
  if (loading) return <p className="text-muted-foreground">Loading…</p>
  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
        No project assignments under this employment.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Project</Th>
            <Th>Role</Th>
            <Th>Start</Th>
            <Th>End</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40">
              <Td className="text-sm">
                <Link to={`/admin/consulting/projects/${a.project}`} className="text-blue-600 hover:underline">
                  {a.project_code} — {a.project_name}
                </Link>
              </Td>
              <Td className="text-xs">{a.role}</Td>
              <Td className="text-xs">{a.start_date}</Td>
              <Td className="text-xs">{a.end_date || <Dash />}</Td>
              <Td>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${ASSIGNMENT_STATUS_COLOR[a.status]}`}>
                  {a.status}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────── Compensation tab ───────────────────

function CompensationTab({
  employmentId, rows, loading,
}: {
  employmentId: number
  rows: Compensation[]
  loading: boolean
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Compensation | null>(null)
  const [form, setForm] = useState<CompInput>(emptyCompDraft())

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        compensation_type: editing.compensation_type,
        amount: editing.amount,
        currency_code: editing.currency_code,
        frequency: editing.frequency,
        gross_net: editing.gross_net,
        effective_from: editing.effective_from,
        effective_to: editing.effective_to,
        reason: editing.reason,
        notes: editing.notes,
      } : emptyCompDraft())
    }
  }, [open, editing])

  const upsertMut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, employment: employmentId }
      if (editing) {
        return (await api.patch<Compensation>(`/hr/compensations/${editing.id}/`, payload)).data
      }
      return (await api.post<Compensation>('/hr/compensations/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Compensation updated' : 'Compensation recorded')
      qc.invalidateQueries({ queryKey: ['hr', 'compensations'] })
      setOpen(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/compensations/${id}/`),
    onSuccess: () => {
      toast.success('Compensation deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'compensations'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function openCreate() { setEditing(null); setOpen(true) }
  function openEdit(c: Compensation) { setEditing(c); setOpen(true) }
  const set = <K extends keyof CompInput>(k: K, v: CompInput[K]) => setForm((f) => ({ ...f, [k]: v }))

  return (
    <div>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Salary history for this employment. Each raise or change is a new row.
        </p>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add compensation</Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No compensation recorded for this employment yet.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Type</Th>
                <Th>Amount</Th>
                <Th>G/N</Th>
                <Th>Frequency</Th>
                <Th>Effective from</Th>
                <Th>Effective to</Th>
                <Th>Reason</Th>
                <Th>Current</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-muted/40">
                  <Td className="text-xs">{COMP_TYPE_LABEL[c.compensation_type]}</Td>
                  <Td className="text-sm font-medium">
                    {Number(c.amount).toLocaleString()} {c.currency_code}
                  </Td>
                  <Td className="text-xs">{COMP_GROSS_NET_LABEL[c.gross_net]}</Td>
                  <Td className="text-xs">{COMP_FREQUENCY_LABEL[c.frequency]}</Td>
                  <Td className="text-xs">{c.effective_from}</Td>
                  <Td className="text-xs">{c.effective_to || <Dash />}</Td>
                  <Td className="text-xs">
                    {c.reason ? COMP_REASON_LABEL[c.reason] || c.reason : <Dash />}
                  </Td>
                  <Td>
                    {c.is_currently_active ? (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <RowActions
                      onEdit={() => openEdit(c)}
                      onDelete={() => confirm(
                        `compensation ${COMP_TYPE_LABEL[c.compensation_type]} from ${c.effective_from}`,
                        () => deleteMut.mutate(c.id),
                      )}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null) }}
        title={editing ? 'Edit compensation' : 'Add compensation'}
        description="Record a new pay decision. Use the same Type for a raise on an existing line, and set the previous row's Effective to."
        onSubmit={() => upsertMut.mutate()}
        isPending={upsertMut.isPending}
        submitLabel={editing ? 'Save' : 'Add'}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="c-type">Type</FieldLabel>
              <Select value={form.compensation_type}
                onValueChange={(v) => set('compensation_type', v as CompInput['compensation_type'])}>
                <SelectTrigger id="c-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(COMP_TYPE_LABEL) as CompInput['compensation_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{COMP_TYPE_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="c-gn">Gross / Net</FieldLabel>
              <Select value={form.gross_net}
                onValueChange={(v) => set('gross_net', v as CompInput['gross_net'])}>
                <SelectTrigger id="c-gn"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(COMP_GROSS_NET_LABEL) as CompInput['gross_net'][]).map((k) => (
                    <SelectItem key={k} value={k}>{COMP_GROSS_NET_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="c-freq">Frequency</FieldLabel>
            <Select value={form.frequency}
              onValueChange={(v) => set('frequency', v as CompInput['frequency'])}>
              <SelectTrigger id="c-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(COMP_FREQUENCY_LABEL) as CompInput['frequency'][]).map((k) => (
                  <SelectItem key={k} value={k}>{COMP_FREQUENCY_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field className="col-span-2">
              <FieldLabel htmlFor="c-amount">Amount *</FieldLabel>
              <Input id="c-amount" type="number" step="0.01" min="0" required
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="c-currency">Currency</FieldLabel>
              <Input id="c-currency" maxLength={3} value={form.currency_code}
                onChange={(e) => set('currency_code', e.target.value.toUpperCase())}
                className="font-mono uppercase" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="c-from">Effective from *</FieldLabel>
              <DatePicker id="c-from" required value={form.effective_from}
                onChange={(v) => set('effective_from', v)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="c-to">Effective to</FieldLabel>
              <DatePicker id="c-to" value={form.effective_to || ''}
                onChange={(v) => set('effective_to', v || null)} />
              <FieldDescription>Leave empty if this is the current pay.</FieldDescription>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="c-reason">Reason</FieldLabel>
            <Select value={form.reason || '__none__'}
              onValueChange={(v) => set('reason', v === '__none__' ? '' : v)}>
              <SelectTrigger id="c-reason"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {Object.entries(COMP_REASON_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="c-notes">Notes</FieldLabel>
            <Textarea id="c-notes" rows={2} value={form.notes}
              onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function emptyCompDraft(): CompInput {
  return {
    compensation_type: 'SALARY',
    amount: '',
    currency_code: 'EUR',
    frequency: 'ANNUAL',
    gross_net: 'GROSS',
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: null,
    reason: '',
    notes: '',
  }
}

// ─────────────────── Leave Balance tab ───────────────────

function LeaveBalanceTab({
  employmentId, rows, loading,
}: {
  employmentId: number
  rows: LeaveBalance[]
  loading: boolean
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<LeaveBalance | null>(null)
  const [form, setForm] = useState<LeaveBalanceInput>(emptyBalanceDraft())

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        year: editing.year,
        leave_type: editing.leave_type,
        entitlement_days: editing.entitlement_days,
        carried_over_days: editing.carried_over_days,
        adjusted_days: editing.adjusted_days,
        notes: editing.notes,
      } : emptyBalanceDraft())
    }
  }, [open, editing])

  const upsertMut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, employment: employmentId }
      if (editing) {
        return (await api.patch<LeaveBalance>(`/hr/leave-balances/${editing.id}/`, payload)).data
      }
      return (await api.post<LeaveBalance>('/hr/leave-balances/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Balance updated' : 'Balance added')
      qc.invalidateQueries({ queryKey: ['hr', 'leave-balances'] })
      setOpen(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/leave-balances/${id}/`),
    onSuccess: () => {
      toast.success('Balance deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'leave-balances'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function openCreate() { setEditing(null); setOpen(true) }
  function openEdit(b: LeaveBalance) { setEditing(b); setOpen(true) }
  const set = <K extends keyof LeaveBalanceInput>(k: K, v: LeaveBalanceInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  return (
    <div>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Per-year leave entitlements. <span className="font-medium">Taken</span> and
          {' '}<span className="font-medium">Balance</span> are computed from approved Time Off records.
        </p>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add balance</Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No leave balances yet. Add one to start tracking annual entitlement.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Year</Th>
                <Th>Type</Th>
                <Th>Entitlement</Th>
                <Th>Carried over</Th>
                <Th>Adjusted</Th>
                <Th>Taken</Th>
                <Th>Balance</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((b) => {
                const balance = Number(b.balance_days)
                const negative = balance < 0
                return (
                  <tr key={b.id} className="hover:bg-muted/40">
                    <Td className="font-mono text-xs">{b.year}</Td>
                    <Td className="text-xs">{LEAVE_LABEL_FULL[b.leave_type]}</Td>
                    <Td className="text-sm">{b.entitlement_days}</Td>
                    <Td className="text-sm">{b.carried_over_days}</Td>
                    <Td className="text-sm">{b.adjusted_days}</Td>
                    <Td className="text-sm">{b.taken_days}</Td>
                    <Td className={`text-sm font-semibold ${negative ? 'text-red-700' : 'text-emerald-700'}`}>
                      {b.balance_days}
                    </Td>
                    <Td className="text-right">
                      <RowActions
                        onEdit={() => openEdit(b)}
                        onDelete={() => confirm(
                          `${LEAVE_LABEL_FULL[b.leave_type]} balance for ${b.year}`,
                          () => deleteMut.mutate(b.id),
                        )}
                      />
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null) }}
        title={editing ? 'Edit leave balance' : 'Add leave balance'}
        description="Each row represents one leave type for one calendar year on this employment."
        onSubmit={() => upsertMut.mutate()}
        isPending={upsertMut.isPending}
        submitLabel={editing ? 'Save' : 'Add'}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="b-year">Year *</FieldLabel>
              <Input id="b-year" type="number" required min={1900} max={2200}
                value={form.year}
                onChange={(e) => set('year', Number(e.target.value))} />
            </Field>
            <Field>
              <FieldLabel htmlFor="b-type">Leave type</FieldLabel>
              <Select value={form.leave_type}
                onValueChange={(v) => set('leave_type', v as LeaveBalanceInput['leave_type'])}>
                <SelectTrigger id="b-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(LEAVE_LABEL_FULL) as LeaveBalanceInput['leave_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{LEAVE_LABEL_FULL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="b-ent">Entitlement *</FieldLabel>
              <Input id="b-ent" type="number" step="0.5" min="0" required
                value={form.entitlement_days}
                onChange={(e) => set('entitlement_days', e.target.value)} />
              <FieldDescription>Statutory / contractual days.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="b-carry">Carried over</FieldLabel>
              <Input id="b-carry" type="number" step="0.5" min="0"
                value={form.carried_over_days}
                onChange={(e) => set('carried_over_days', e.target.value)} />
              <FieldDescription>From the previous year.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="b-adj">Adjusted</FieldLabel>
              <Input id="b-adj" type="number" step="0.5"
                value={form.adjusted_days}
                onChange={(e) => set('adjusted_days', e.target.value)} />
              <FieldDescription>+ grant or − deduct.</FieldDescription>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="b-notes">Notes</FieldLabel>
            <Textarea id="b-notes" rows={2} value={form.notes}
              onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function emptyBalanceDraft(): LeaveBalanceInput {
  return {
    year: new Date().getFullYear(),
    leave_type: 'VACATION',
    entitlement_days: '25',
    carried_over_days: '0',
    adjusted_days: '0',
    notes: '',
  }
}

// ─────────────────── Documents tab ───────────────────

function DocumentsTab({
  employmentId, rows, loading,
}: {
  employmentId: number
  rows: EmploymentDocument[]
  loading: boolean
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<EmploymentDocument | null>(null)
  const [form, setForm] = useState<DocInput>(emptyDocDraft())

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        document_type: editing.document_type,
        title: editing.title,
        file_url: editing.file_url,
        issued_date: editing.issued_date,
        expiry_date: editing.expiry_date,
        notes: editing.notes,
      } : emptyDocDraft())
    }
  }, [open, editing])

  const upsertMut = useMutation({
    mutationFn: async () => {
      const payload = { ...form, employment: employmentId }
      if (editing) {
        return (await api.patch<EmploymentDocument>(`/hr/employment-documents/${editing.id}/`, payload)).data
      }
      return (await api.post<EmploymentDocument>('/hr/employment-documents/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Document updated' : 'Document added')
      qc.invalidateQueries({ queryKey: ['hr', 'employment-documents'] })
      setOpen(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/employment-documents/${id}/`),
    onSuccess: () => {
      toast.success('Document deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'employment-documents'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function openCreate() { setEditing(null); setOpen(true) }
  function openEdit(d: EmploymentDocument) { setEditing(d); setOpen(true) }
  const set = <K extends keyof DocInput>(k: K, v: DocInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  return (
    <div>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Files attached to this employment. Paste a Drive / Dropbox / S3 URL — managed file uploads come later.
        </p>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add document</Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No documents yet on this employment.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Type</Th>
                <Th>Title</Th>
                <Th>Issued</Th>
                <Th>Expires</Th>
                <Th>Status</Th>
                <Th>File</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((d) => (
                <tr key={d.id} className="hover:bg-muted/40">
                  <Td className="text-xs">{DOC_TYPE_LABEL[d.document_type]}</Td>
                  <Td className="text-sm">
                    <div className="font-medium text-foreground">{d.title}</div>
                    {d.notes && <div className="text-xs text-muted-foreground line-clamp-1">{d.notes}</div>}
                  </Td>
                  <Td className="text-xs">{d.issued_date || <Dash />}</Td>
                  <Td className="text-xs">{d.expiry_date || <Dash />}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${DOC_EXPIRY_COLOR[d.expiry_status]}`}>
                      {DOC_EXPIRY_LABEL[d.expiry_status]}
                    </span>
                  </Td>
                  <Td className="text-sm">
                    {d.file_url ? (
                      <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 hover:underline truncate inline-block max-w-[200px]">
                        Open
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-xs">No file</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <RowActions
                      onEdit={() => openEdit(d)}
                      onDelete={() => confirm(
                        `${DOC_TYPE_LABEL[d.document_type]} — ${d.title}`,
                        () => deleteMut.mutate(d.id),
                      )}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null) }}
        title={editing ? 'Edit document' : 'Add document'}
        description="Attach a document to this employment by recording its type, title, and a link to where the file lives."
        onSubmit={() => upsertMut.mutate()}
        isPending={upsertMut.isPending}
        submitLabel={editing ? 'Save' : 'Add'}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="d-type">Type</FieldLabel>
              <Select value={form.document_type}
                onValueChange={(v) => set('document_type', v as DocInput['document_type'])}>
                <SelectTrigger id="d-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(DOC_TYPE_LABEL) as DocInput['document_type'][]).map((k) => (
                    <SelectItem key={k} value={k}>{DOC_TYPE_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="d-title">Title *</FieldLabel>
              <Input id="d-title" required value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Offer letter signed 2025-09-12" />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="d-url">File URL</FieldLabel>
            <Input id="d-url" type="url" value={form.file_url}
              onChange={(e) => set('file_url', e.target.value)}
              placeholder="https://drive.google.com/…" />
            <FieldDescription>Drive / Dropbox / S3 link to the document.</FieldDescription>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="d-issued">Issued date</FieldLabel>
              <DatePicker id="d-issued" value={form.issued_date || ''}
                onChange={(v) => set('issued_date', v || null)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="d-expiry">Expiry date</FieldLabel>
              <DatePicker id="d-expiry" value={form.expiry_date || ''}
                onChange={(v) => set('expiry_date', v || null)} />
              <FieldDescription>Set for IDs / certifications. Leave empty otherwise.</FieldDescription>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="d-notes">Notes</FieldLabel>
            <Textarea id="d-notes" rows={2} value={form.notes}
              onChange={(e) => set('notes', e.target.value)} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function emptyDocDraft(): DocInput {
  return {
    document_type: 'CONTRACT',
    title: '',
    file_url: '',
    issued_date: null,
    expiry_date: null,
    notes: '',
  }
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}
